import { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { haptic } from '../utils/haptic';

const THRESHOLD = 80;
const MAX_PULL = 120;

export const PullToRefresh = () => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const location = useLocation();

  const isAtTop = () => window.scrollY <= 0;

  const onTouchStart = useCallback((e) => {
    if (isAtTop() && !refreshing) {
      startY.current = e.touches[0].clientY;
      setPulling(true);
    }
  }, [refreshing]);

  const onTouchMove = useCallback((e) => {
    if (!pulling || refreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0 && isAtTop()) {
      setPullDistance(Math.min(delta * 0.5, MAX_PULL));
    }
  }, [pulling, refreshing]);

  const onTouchEnd = useCallback(() => {
    if (!pulling) return;
    if (pullDistance >= THRESHOLD && !refreshing) {
      setRefreshing(true);
      setPullDistance(THRESHOLD * 0.6);
      haptic('success');
      setTimeout(() => window.location.reload(), 600);
    } else {
      setPullDistance(0);
    }
    setPulling(false);
  }, [pulling, pullDistance, refreshing]);

  useEffect(() => {
    // Only on mobile
    const mq = window.matchMedia('(max-width: 767px)');
    if (!mq.matches) return;

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [onTouchStart, onTouchMove, onTouchEnd]);

  // Reset on navigation
  useEffect(() => {
    setPullDistance(0);
    setRefreshing(false);
    setPulling(false);
  }, [location.pathname]);

  if (pullDistance <= 0) return null;

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const rotation = pulling ? pullDistance * 3 : 0;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[10001] flex justify-center pointer-events-none md:hidden"
      style={{ transform: `translateY(${pullDistance - 40}px)`, transition: pulling ? 'none' : 'transform 0.3s ease-out' }}
      data-testid="pull-to-refresh"
    >
      <div
        className="w-9 h-9 rounded-full bg-white shadow-lg flex items-center justify-center border border-black/5"
        style={{ opacity: progress }}
      >
        {refreshing ? (
          <svg className="w-4 h-4 animate-spin text-bronze" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <svg
            className="w-4 h-4 text-obsidian"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            style={{ transform: `rotate(${rotation}deg)`, transition: pulling ? 'none' : 'transform 0.3s' }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        )}
      </div>
    </div>
  );
};
